import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { money, moneyCompact, num, fmtDate, daysLabel } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Sunrise, Hourglass, GitBranch, ArrowRight, Inbox, BellRing, AtSign, X, Zap, Flame, Snowflake, ImagePlus, Timer, Layers, TrendingUp, ListChecks, FileWarning, Reply, Clock } from "lucide-react";
import { ResumeRail } from "@/components/ResumeRail";
import { StatCard } from "@/components/ui/StatCard";
import { kindIcon, dueTone } from "@/components/ActivityPanel";
import { CostDonut } from "@/components/charts/ChartKit";
import { toast } from "sonner";
import { OutcomeReview } from "@/components/OutcomeReview";
import { TrayCount } from "@/components/bd/TrayCount";
import { useSyncedRefresh } from "@/hooks/useLiveSync";
import { DeadlineChip, DueDates } from "@/components/BidOpsChips";
import { AlarmClock } from "lucide-react";
import LiveReads from "@/components/LiveReads";

const agingChip = (d) => {
  if (d === null || d === undefined) return "bg-slate-100 text-slate-500";
  if (d > 90) return "bg-rose-100 text-rose-700";
  if (d > 30) return "bg-amber-100 text-amber-700";
  return "bg-emerald-50 text-emerald-700";
};

export default function Today() {
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const [waiting, setWaiting] = useState([]);
  const [pipeline, setPipeline] = useState([]);
  const [mentions, setMentions] = useState([]);
  const [replyTo, setReplyTo] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [nudges, setNudges] = useState({ chase: [], quiet: [] });
  const [followups, setFollowups] = useState({ overdue: [], today: [], upcoming: [] });
  const [quoteAlerts, setQuoteAlerts] = useState([]);
  const [heat, setHeat] = useState(null);
  const [skipTarget, setSkipTarget] = useState(null);   // {id, name} of the follow-up being skipped
  const [skipReason, setSkipReason] = useState("");
  const [deadlines, setDeadlines] = useState([]);

  const reloadAll = () => {
    api.get("/ops/deadlines", { params: { days: 3 } }).then((r) => setDeadlines(r.data?.items || [])).catch(() => {});
    api.get("/pipeline/waiting").then((r) => setWaiting(r.data)).catch(() => {});
    api.get("/pipeline").then((r) => setPipeline(r.data)).catch(() => {});
    api.get("/nudges").then((r) => setNudges(r.data || { chase: [], quiet: [] })).catch(() => {});
    api.get("/crm/followups").then((r) => setFollowups(r.data || { overdue: [], today: [], upcoming: [] })).catch(() => {});
    api.get("/crm/quote-alerts").then((r) => setQuoteAlerts(r.data?.alerts || [])).catch(() => {});
  };
  useEffect(() => {
    reloadAll();
    api.get("/mentions").then((r) => { setMentions(r.data.mentions || []); if (r.data.unread) api.post("/mentions/seen").catch(() => {}); }).catch(() => {});
    api.get("/insights/win-heatmap").then((r) => setHeat(r.data)).catch(() => {});
  }, []);
  useSyncedRefresh(reloadAll);
  const HEAT_C = { hot: "#E11D48", warm: "#F59E0B", cool: "#0EA5E9", cold: "#64748B" };

  const completeFollowup = async (a) => {
    try {
      await api.post(`/crm/activities/${a.activity_id}/done`, { done: true });
      setFollowups((f) => ({ ...f, overdue: f.overdue.filter((x) => x.activity_id !== a.activity_id), today: f.today.filter((x) => x.activity_id !== a.activity_id) }));
      toast.success("Marked done");
    } catch { toast.error("Could not update"); }
  };
  const goEntity = (a) => navigate(a.entity_type === "bid" ? `/bids/${a.entity_id}` : "/pipeline");

  const dismissMention = (m) => {
    setMentions((ms) => ms.filter((x) => x.comment_id !== m.comment_id));
    api.post(`/mentions/${m.comment_id}/dismiss`).catch(() => {});
  };
  const goMention = (m) => { dismissMention(m); navigate(m.entity_type === "bid" ? `/bids/${m.entity_id}` : "/clients"); };
  const sendReply = async (m) => {
    const t = replyText.trim(); if (!t) return;
    try {
      await api.post("/comments", { entity_type: m.entity_type, entity_id: m.entity_id, text: t, mentions: m.author_id ? [m.author_id] : [] });
      toast.success(`Replied on ${m.label}`); setReplyTo(null); setReplyText("");
    } catch { toast.error("Could not send reply"); }
  };

  // Remove a project from every follow-up list on the page once it's skipped/snoozed.
  const removeFollowup = (id) => {
    setWaiting((w) => w.map((x) => (x.bid_id === id ? { ...x, owed: false } : x)));
    setNudges((n) => ({ chase: n.chase.filter((x) => x.bid_id !== id), quiet: n.quiet.filter((x) => x.bid_id !== id) }));
  };
  const doSkip = async () => {
    if (!skipTarget) return;
    try {
      await api.post(`/bids/${skipTarget.id}/followup/skip`, { reason: skipReason.trim() });
      toast.success("Follow-up skipped — it won't nag again unless they email you");
      removeFollowup(skipTarget.id); setSkipTarget(null); setSkipReason("");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not skip"); }
  };
  const snoozeFollowup = async (id, days = 14) => {
    try {
      await api.post(`/bids/${id}/followup/snooze`, { days });
      toast.success(`Snoozed for ${days} days — we'll nudge you again then`);
      removeFollowup(id);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not snooze"); }
  };

  const intake = pipeline.filter((i) => i.kind === "lead" && i.status === "new");
  const pursuing = pipeline.filter((i) => i.kind === "lead" && i.status === "qualified");
  const owed = waiting.filter((b) => b.owed);
  const waitingValue = waiting.reduce((a, b) => a + (b.stored_total || 0), 0);
  const firstName = (user?.name || "there").split(" ")[0];

  // Pipeline snapshot for the "at a glance" donut + probability-weighted forecast.
  const stageProb = { new: 0.05, review: 0.1, qualified: 0.2, estimating: 0.35, proposal: 0.5, sent: 0.6 };
  const glance = [
    { label: "New / review", value: pipeline.filter((i) => i.kind === "lead" && ["new", "review"].includes(i.status)).length, color: "#94A3B8" },
    { label: "Qualified", value: pipeline.filter((i) => i.kind === "lead" && i.status === "qualified").length, color: "#F4C400" },
    { label: "Estimating", value: pipeline.filter((i) => i.kind === "bid" && i.status === "estimating").length, color: "#F59E0B" },
    { label: "Proposal", value: pipeline.filter((i) => i.kind === "bid" && i.status === "proposal").length, color: "#6366F1" },
    { label: "Sent", value: pipeline.filter((i) => i.kind === "bid" && i.status === "sent").length, color: "#003E87" },
  ].filter((d) => d.value > 0);
  const glanceTotal = glance.reduce((a, b) => a + b.value, 0);
  const weightedForecast = pipeline.reduce((s, it) => s + (it.stored_total || 0) * (stageProb[it.status] || 0), 0);

  // Gentle, low-priority prompt for users with no profile photo (Google avatar OR uploaded).
  const [avatarNudgeHidden, setAvatarNudgeHidden] = useState(false);
  const showAvatarNudge = !!user && !user.picture && !user.avatar_path && !avatarNudgeHidden &&
    (() => { try { return localStorage.getItem(`avatarNudgeDismissed:${user.user_id}`) !== "1"; } catch { return true; } })();
  const dismissAvatarNudge = () => { try { localStorage.setItem(`avatarNudgeDismissed:${user.user_id}`, "1"); } catch { /* ignore */ } setAvatarNudgeHidden(true); };
  const avatarFileRef = useRef(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const uploadAvatar = async (file) => {
    if (!file) return;
    setUploadingAvatar(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post("/auth/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Profile photo added"); refresh?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setUploadingAvatar(false); if (avatarFileRef.current) avatarFileRef.current.value = ""; }
  };

  const Metric = ({ label, value, sub }) => (
    <div>
      <div className="font-display text-2xl font-bold text-black tabular-nums leading-none">{value}</div>
      <div className="text-[11px] uppercase tracking-wider text-slate-400 mt-1">{label}</div>
      {sub && <div className="text-[11px] text-slate-400">{sub}</div>}
    </div>
  );
  return (
    <div className="px-6 sm:px-8 py-8 max-w-[1800px] w-full mx-auto">
      <div className="flex items-center gap-2 mb-1">
        <Sunrise className="w-5 h-5 text-[#F4C400]" />
        <h1 className="font-display text-2xl font-bold tracking-tight text-black">Good day, {firstName}.</h1>
      </div>
      <p className="text-sm text-slate-500 mb-6">Here's what's owed and what's waiting across business development.</p>
      <LiveReads />

      <ResumeRail />

      <TrayCount />
      <OutcomeReview />

      {showAvatarNudge && (
        <div data-testid="avatar-nudge"
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); uploadAvatar(e.dataTransfer.files?.[0]); }}
          className="mb-6 flex items-center gap-3 rounded-xl border border-dashed border-[#003E87]/30 bg-[#003E87]/[0.04] px-4 py-3">
          <input ref={avatarFileRef} type="file" accept="image/*" className="hidden" data-testid="avatar-nudge-input" onChange={(e) => uploadAvatar(e.target.files?.[0])} />
          <div className="w-9 h-9 rounded-full bg-[#003E87]/10 text-[#003E87] flex items-center justify-center shrink-0"><ImagePlus className="w-4 h-4" /></div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium text-slate-800">Add a profile photo</div>
            <div className="text-xs text-slate-500">Drag a photo here, or click — so teammates can see who marked up what.</div>
          </div>
          <Button size="sm" disabled={uploadingAvatar} className="bg-[#003E87] hover:bg-[#003E87]/90 text-white text-xs" data-testid="avatar-nudge-add" onClick={() => avatarFileRef.current?.click()}>{uploadingAvatar ? "Adding…" : "Add a photo"}</Button>
          <button data-testid="avatar-nudge-dismiss" onClick={dismissAvatarNudge} title="Not now" className="text-slate-400 hover:text-slate-600 shrink-0"><X className="w-4 h-4" /></button>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6 stagger-children">
        <StatCard testid="kpi-bids-waiting" icon={Hourglass} accent="#003E87" label="Bids waiting" value={waiting.length} sub={moneyCompact(waitingValue) + " in play"} onClick={() => navigate("/awaiting")} />
        <StatCard testid="kpi-new-intake" icon={GitBranch} accent="#F4C400" label="New intake" value={intake.length} sub="need a decision" onClick={() => navigate("/pipeline")} />
        <StatCard testid="kpi-pursuing" icon={TrendingUp} accent="#10B981" label="Pursuing" value={pursuing.length} sub="ready to convert" onClick={() => navigate("/pipeline")} />
        <StatCard testid="kpi-oldest-wait" icon={Timer} accent="#F59E0B" label="Oldest wait" value={waiting[0]?.days_waiting != null ? `${waiting[0].days_waiting}d` : "—"} sub={waiting[0]?.name || "nothing waiting"} />
      </div>

      {/* Wide screens: two columns that fill the display; narrow: one. */}
      <div className="xl:columns-2 xl:gap-x-8 [&>*]:break-inside-avoid" data-testid="today-columns">
      {deadlines.length > 0 && (
        <section className="mb-8" data-testid="today-deadlines">
          <div className="flex items-center gap-2 mb-3">
            <AlarmClock className="w-4 h-4 text-orange-600" />
            <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-600">Internal deadlines</h2>
            <span className="text-xs text-slate-400 font-mono">{deadlines.length}</span>
          </div>
          <div className="space-y-2">
            {deadlines.slice(0, 8).map((d) => (
              <Card key={d.bid_id} data-testid={`today-deadline-${d.bid_id}`} onClick={() => navigate(`/bids/${d.bid_id}`)}
                className={`flex items-center gap-3 p-3 cursor-pointer hover:shadow-sm transition ${d.flag === "past" ? "border-rose-200 bg-rose-50/40" : d.flag ? "border-amber-200 bg-amber-50/30" : ""}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-black truncate">{d.name}</div>
                  <div className="text-xs text-slate-500 truncate">{d.client_name || "No client"}{d.assignee?.name ? ` · ${d.assignee.name}` : ""}</div>
                  <DueDates due={d.due_date} internal={d.internal_due_date} testid={`today-deadline-dates-${d.bid_id}`} />
                </div>
                {d.flag || d.internal_flag ? <DeadlineChip flag={d.flag} internalFlag={d.internal_flag} testid={`today-deadline-flag-${d.bid_id}`} /> : <span className="text-xs text-slate-500 shrink-0">in {d.days}d</span>}
              </Card>
            ))}
          </div>
        </section>
      )}

      {heat?.pursuits?.length > 0 && (
        <section className="mb-8" data-testid="today-heatmap">
          <div className="flex items-center gap-2 mb-3">
            <Flame className="w-4 h-4 text-[#E11D48]" />
            <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-600">Hottest pursuits</h2>
            <button onClick={() => navigate("/heat-map")} className="ml-auto text-xs text-[#003E87] hover:underline flex items-center gap-1">Full heat map <ArrowRight className="w-3 h-3" /></button>
          </div>
          <div className="space-y-2">
            {heat.pursuits.slice(0, 3).map((p, i) => {
              const c = HEAT_C[p.heat] || HEAT_C.cold;
              return (
                <Card key={p.bid_id} data-testid={`today-heat-${p.bid_id}`} className="flex items-center gap-3 p-3 cursor-pointer hover:shadow-sm transition overflow-hidden" onClick={() => navigate(`/bids/${p.bid_id}?tab=context`)}>
                  <span className="w-1.5 self-stretch rounded-full shrink-0" style={{ backgroundColor: c }} />
                  <span className="text-[11px] font-bold text-slate-300 tabular-nums w-4 shrink-0">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-black truncate">{p.name}</div>
                    <div className="text-xs text-slate-500 truncate">{p.client || "No client"}{p.reasons?.[0] ? ` · ${p.reasons[0]}` : ""}</div>
                  </div>
                  <div className="font-display text-xl font-bold tabular-nums shrink-0" style={{ color: c }}>{p.score}</div>
                </Card>
              );
            })}
          </div>
        </section>
      )}

      {glance.length > 0 && (
        <Card className="p-5 mb-8" data-testid="today-pipeline-glance">
          <div className="flex items-center gap-2 mb-3">
            <Layers className="w-4 h-4 text-[#003E87]" />
            <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-600">Pipeline at a glance</h2>
            <button onClick={() => navigate("/pipeline")} className="ml-auto text-xs text-[#003E87] hover:underline flex items-center gap-1">Open pipeline <ArrowRight className="w-3 h-3" /></button>
          </div>
          <div className="grid sm:grid-cols-2 gap-6 items-center">
            <CostDonut data={glance} valueFmt={(v) => `${v}`} centerLabel="Pursuits" centerValue={glanceTotal} height={190} />
            <div>
              <div className="text-[11px] uppercase tracking-wider text-slate-400">Weighted forecast</div>
              <div className="font-mono text-3xl font-bold text-slate-900 tabular-nums leading-none mt-1">{moneyCompact(weightedForecast)}</div>
              <div className="text-xs text-slate-400 mt-2">Probability-weighted value across every open pursuit — a realistic view of what's likely to land.</div>
            </div>
          </div>
        </Card>
      )}

      {(followups.overdue.length > 0 || followups.today.length > 0) && (
        <section className="mb-8" data-testid="today-followups">
          <div className="flex items-center gap-2 mb-3">
            <ListChecks className="w-4 h-4 text-[#003E87]" />
            <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-600">Follow-ups due</h2>
            <span className="text-xs text-slate-400 font-mono">{followups.overdue.length + followups.today.length}</span>
          </div>
          <div className="space-y-2">
            {[...followups.overdue, ...followups.today].slice(0, 8).map((a) => {
              const Icon = kindIcon(a.kind);
              return (
                <Card key={a.activity_id} data-testid={`today-followup-${a.activity_id}`} className={`flex items-center gap-3 p-3 ${a.days < 0 ? "border-rose-200 bg-rose-50/40" : "border-amber-200 bg-amber-50/30"}`}>
                  <button onClick={() => completeFollowup(a)} title="Mark done" data-testid={`today-followup-done-${a.activity_id}`} className="w-5 h-5 rounded border border-slate-300 hover:border-emerald-500 hover:bg-emerald-50 grid place-items-center shrink-0 transition-colors"><Icon className="w-3 h-3 text-[#003E87]" /></button>
                  <div className="flex-1 min-w-0 cursor-pointer" onClick={() => goEntity(a)}>
                    <div className="text-sm font-medium text-black truncate">{a.subject}</div>
                    <div className="text-xs text-slate-500 truncate">{a.entity_name || "—"}{a.client_name ? ` · ${a.client_name}` : ""}</div>
                  </div>
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border shrink-0 ${dueTone(a.days)}`}>{daysLabel(a.days)}</span>
                </Card>
              );
            })}
          </div>
        </section>
      )}

      {quoteAlerts.length > 0 && (
        <section className="mb-8" data-testid="today-quote-alerts">
          <div className="flex items-center gap-2 mb-3">
            <FileWarning className="w-4 h-4 text-rose-500" />
            <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-600">Quotes expiring</h2>
            <span className="text-xs text-slate-400 font-mono">{quoteAlerts.length}</span>
          </div>
          <div className="space-y-2">
            {quoteAlerts.slice(0, 6).map((a, i) => (
              <Card key={i} data-testid={`today-quote-alert-${i}`} className={`flex items-center gap-3 p-3 cursor-pointer hover:shadow-sm transition ${a.status === "expired" ? "border-rose-200 bg-rose-50/40" : "border-amber-200 bg-amber-50/30"}`} onClick={() => navigate(`/bids/${a.bid_id}?tab=leveling`)}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-black truncate">{a.vendor_name} · {a.bid_name}</div>
                  <div className="text-xs text-slate-500 truncate">{money(a.amount)}{a.client_name ? ` · ${a.client_name}` : ""}</div>
                </div>
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full shrink-0 ${a.status === "expired" ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-700"}`}>{a.status === "expired" ? `expired ${Math.abs(a.days)}d ago` : `${a.days}d left`}</span>
              </Card>
            ))}
          </div>
        </section>
      )}

      {mentions.length > 0 && (
        <section className="mb-8" data-testid="today-mentions">
          <div className="flex items-center gap-2 mb-3">
            <AtSign className="w-4 h-4 text-[#003E87]" />
            <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-600">Mentions &amp; follow-ups</h2>
            <span className="text-xs text-slate-400 font-mono">{mentions.length}</span>
          </div>
          <div className="space-y-2">
            {mentions.slice(0, 6).map((m) => (
              <Card key={m.comment_id} data-testid={`today-mention-${m.comment_id}`}
                className={`p-3 ${m.unread ? "border-[#003E87]/40 bg-[#003E87]/[0.03]" : ""}`}>
                <div className="flex items-start gap-3">
                  <div className="w-7 h-7 rounded-full bg-[#003E87]/10 text-[#003E87] grid place-items-center text-[11px] font-semibold shrink-0 mt-0.5">{(m.author_name || "?").slice(0, 2).toUpperCase()}</div>
                  <div className="flex-1 min-w-0 cursor-pointer" onClick={() => goMention(m)}>
                    <div className="text-sm text-black truncate"><b>{m.author_name}</b> mentioned you on <span className="text-[#003E87]">{m.label}</span></div>
                    <div className="text-xs text-slate-500 truncate">{m.text}</div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button data-testid={`mention-reply-btn-${m.comment_id}`} onClick={() => { setReplyTo(replyTo === m.comment_id ? null : m.comment_id); setReplyText(""); }}
                      className="text-[11px] text-[#003E87] hover:underline">Reply</button>
                    <div className="text-[11px] text-slate-400">{fmtDate(m.created_at)}</div>
                    <button data-testid={`mention-dismiss-${m.comment_id}`} onClick={() => dismissMention(m)} title="Dismiss" className="text-slate-300 hover:text-rose-600"><X className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
                {replyTo === m.comment_id && (
                  <div className="flex items-end gap-2 mt-2 pl-10" data-testid={`mention-reply-box-${m.comment_id}`}>
                    <textarea autoFocus value={replyText} onChange={(e) => setReplyText(e.target.value)} rows={2}
                      onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) sendReply(m); }}
                      placeholder={`Reply to ${m.author_name}…`} className="flex-1 text-sm border border-slate-200 rounded px-3 py-2 resize-none" data-testid={`mention-reply-input-${m.comment_id}`} />
                    <button onClick={() => sendReply(m)} disabled={!replyText.trim()} data-testid={`mention-reply-send-${m.comment_id}`}
                      className="h-9 px-3 rounded bg-[#003E87] text-white text-sm disabled:opacity-40">Send</button>
                  </div>
                )}
              </Card>
            ))}
          </div>
        </section>
      )}

      {owed.length > 0 && (() => {
        const oweReplies = owed.filter((b) => b.waiting_on !== "client");
        const checkins = owed.filter((b) => b.waiting_on === "client");
        return (
        <section className="mb-8" data-testid="today-owed">
          <div className="flex items-center gap-2 mb-3">
            <BellRing className="w-4 h-4 text-[#F4C400]" />
            <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-600">Owed a follow-up</h2>
            <span className="text-xs text-slate-400 font-mono" data-testid="today-owed-count">{oweReplies.length} repl{oweReplies.length === 1 ? "y" : "ies"} · {checkins.length} check-in{checkins.length === 1 ? "" : "s"}</span>
          </div>
          <div className="space-y-2">
            {owed.slice(0, 8).map((b) => {
              const checkin = b.waiting_on === "client";
              return (
              <Card key={b.bid_id} data-testid={`today-owed-${b.bid_id}`}
                className={`flex items-center gap-3 p-3 transition ${checkin ? "border-amber-200 bg-amber-50/30" : "border-[#F4C400]/40 bg-[#F4C400]/[0.04]"}`}>
                {checkin ? <Clock className="w-4 h-4 text-amber-500 shrink-0" /> : <BellRing className="w-4 h-4 text-[#F4C400] shrink-0" />}
                <div className="flex-1 min-w-0 cursor-pointer" onClick={() => navigate(`/bids/${b.bid_id}?tab=context`)}>
                  <div className="text-sm font-medium text-black truncate">{b.name}</div>
                  <div className={`text-xs truncate ${checkin ? "text-amber-700" : "text-[#8a6d00]"}`}>{checkin ? "Time for a check-in" : "You owe a reply"} · {b.owed_reason || b.client_name || "No client"}</div>
                </div>
                <div className="text-sm font-mono text-slate-700 shrink-0 hidden lg:block">{b.stored_total ? money(b.stored_total) : "—"}</div>
                {checkin && (
                  <Button size="sm" variant="outline" data-testid={`today-owed-snooze-${b.bid_id}`} onClick={() => snoozeFollowup(b.bid_id, 14)} className="shrink-0 hidden sm:inline-flex" title="Snooze this check-in for 2 weeks"><Timer className="w-3.5 h-3.5 mr-1" /> Snooze 2w</Button>
                )}
                <Button size="sm" variant="ghost" data-testid={`today-owed-skip-${b.bid_id}`} onClick={() => { setSkipTarget({ id: b.bid_id, name: b.name }); setSkipReason(""); }} className="shrink-0 text-slate-500 hover:text-rose-600" title="Skip this follow-up (say why)"><X className="w-3.5 h-3.5 mr-1" /> Skip</Button>
                <Button size="sm" data-testid={`today-owed-reply-${b.bid_id}`} onClick={() => navigate(`/bids/${b.bid_id}?tab=context&reply=1`)}
                  className="bg-[#003E87] hover:bg-[#00306a] text-white shrink-0"><Reply className="w-3.5 h-3.5 mr-1" /> {checkin ? "Open" : "Reply"}</Button>
              </Card>
              );
            })}
          </div>
        </section>
        );
      })()}

      {(nudges.chase.length > 0 || nudges.quiet.length > 0) && (
        <section className="mb-8" data-testid="today-nudges">
          <div className="flex items-center gap-2 mb-3">
            <Zap className="w-4 h-4 text-[#F4C400]" />
            <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-600">Act today</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div data-testid="nudge-chase">
              <div className="flex items-center gap-1.5 text-xs font-semibold text-emerald-700 mb-2"><Flame className="w-3.5 h-3.5" /> Worth chasing ({nudges.chase.length})</div>
              {nudges.chase.length === 0 ? (
                <Card className="p-4 text-xs text-slate-400">No responsive clients are overdue a nudge right now.</Card>
              ) : (
                <div className="space-y-2">
                  {nudges.chase.map((n) => (
                    <Card key={n.bid_id} data-testid={`nudge-chase-${n.bid_id}`} className="p-3 hover:border-emerald-300 transition">
                      <div className="cursor-pointer" onClick={() => navigate(`/bids/${n.bid_id}?tab=context&reply=1`)}>
                        <div className="text-sm font-medium text-black truncate">{n.bid_name}</div>
                        <div className="text-xs text-slate-500 truncate">{n.client} · {num(n.capacity_mw)} MW{n.state ? ` · ${n.state}` : ""}</div>
                        <div className="text-[11px] text-emerald-700 mt-1">{n.headline}</div>
                      </div>
                      <div className="flex items-center gap-1 mt-2 pt-2 border-t border-slate-100">
                        <Button size="sm" variant="ghost" className="h-7 text-[11px] text-slate-500 hover:text-amber-700" data-testid={`nudge-snooze-${n.bid_id}`} onClick={(e) => { e.stopPropagation(); snoozeFollowup(n.bid_id, 14); }}><Timer className="w-3 h-3 mr-1" /> Snooze 2w</Button>
                        <Button size="sm" variant="ghost" className="h-7 text-[11px] text-slate-500 hover:text-rose-600" data-testid={`nudge-skip-${n.bid_id}`} onClick={(e) => { e.stopPropagation(); setSkipTarget({ id: n.bid_id, name: n.bid_name }); setSkipReason(""); }}><X className="w-3 h-3 mr-1" /> Skip</Button>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </div>
            <div data-testid="nudge-quiet">
              <div className="flex items-center gap-1.5 text-xs font-semibold text-amber-700 mb-2"><Snowflake className="w-3.5 h-3.5" /> Going quiet ({nudges.quiet.length})</div>
              {nudges.quiet.length === 0 ? (
                <Card className="p-4 text-xs text-slate-400">Nothing's gone cold — nice.</Card>
              ) : (
                <div className="space-y-2">
                  {nudges.quiet.map((n) => (
                    <Card key={n.bid_id} data-testid={`nudge-quiet-${n.bid_id}`} className="p-3 hover:border-amber-300 transition">
                      <div className="cursor-pointer" onClick={() => navigate(`/bids/${n.bid_id}?tab=context&reply=1`)}>
                        <div className="text-sm font-medium text-black truncate">{n.bid_name}</div>
                        <div className="text-xs text-slate-500 truncate">{n.client} · {num(n.capacity_mw)} MW{n.state ? ` · ${n.state}` : ""}</div>
                        <div className="text-[11px] text-amber-700 mt-1">{n.headline}</div>
                      </div>
                      <div className="flex items-center gap-1 mt-2 pt-2 border-t border-slate-100">
                        <Button size="sm" variant="ghost" className="h-7 text-[11px] text-slate-500 hover:text-amber-700" data-testid={`nudge-snooze-${n.bid_id}`} onClick={(e) => { e.stopPropagation(); snoozeFollowup(n.bid_id, 14); }}><Timer className="w-3 h-3 mr-1" /> Snooze 2w</Button>
                        <Button size="sm" variant="ghost" className="h-7 text-[11px] text-slate-500 hover:text-rose-600" data-testid={`nudge-skip-${n.bid_id}`} onClick={(e) => { e.stopPropagation(); setSkipTarget({ id: n.bid_id, name: n.bid_name }); setSkipReason(""); }}><X className="w-3 h-3 mr-1" /> Skip</Button>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      <section className="mb-8" data-testid="today-waiting">
        <div className="flex items-center gap-2 mb-3">
          <Hourglass className="w-4 h-4 text-slate-400" />
          <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-600">Waiting the longest</h2>
          <button onClick={() => navigate("/awaiting")} className="ml-auto text-xs text-[#003E87] hover:underline flex items-center gap-1">All bids waiting <ArrowRight className="w-3 h-3" /></button>
        </div>
        {waiting.length === 0 ? (
          <Card className="p-8 text-center text-sm text-slate-400"><Inbox className="w-6 h-6 mx-auto mb-2 text-slate-300" />Nothing waiting on a decision. Submit a bid and it shows up here.</Card>
        ) : (
          <div className="space-y-2">
            {waiting.slice(0, 5).map((b) => (
              <Card key={b.bid_id} data-testid={`today-waiting-${b.bid_id}`}
                className="flex items-center gap-3 p-3 cursor-pointer hover:border-[#003E87]/40 transition" onClick={() => navigate(`/bids/${b.bid_id}`)}>
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full shrink-0 ${agingChip(b.days_waiting)}`}>{b.days_waiting != null ? `${b.days_waiting}d` : "—"}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-black truncate">{b.name}</div>
                  <div className="text-xs text-slate-500 truncate">{b.client_name || "No client"} · {num(b.capacity_mw)} MW{b.sent_at ? ` · sent ${fmtDate(b.sent_at)}` : ""}</div>
                </div>
                <div className="text-sm font-mono text-slate-700 shrink-0">{b.stored_total ? money(b.stored_total) : "—"}</div>
              </Card>
            ))}
          </div>
        )}
      </section>

      <section className="mb-8" data-testid="today-intake">
        <div className="flex items-center gap-2 mb-3">
          <GitBranch className="w-4 h-4 text-slate-400" />
          <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-600">New intake — decide next</h2>
          <button onClick={() => navigate("/pipeline")} className="ml-auto text-xs text-[#003E87] hover:underline flex items-center gap-1">Open pipeline <ArrowRight className="w-3 h-3" /></button>
        </div>
        {intake.length === 0 ? (
          <Card className="p-6 text-center text-sm text-slate-400">No new leads waiting on a bid decision.</Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {intake.slice(0, 6).map((l) => (
              <Card key={l.id} className="flex items-center gap-3 p-3 cursor-pointer hover:border-[#003E87]/40 transition" onClick={() => navigate("/pipeline")}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-black truncate">{l.name}</div>
                  <div className="text-xs text-slate-500 truncate">{l.subtitle || "No company"} · {num(l.capacity_mw)} MW{l.state ? ` · ${l.state}` : ""}</div>
                </div>
                <Button size="sm" variant="outline" className="shrink-0">Decide</Button>
              </Card>
            ))}
          </div>
        )}
      </section>
      </div>

      <Dialog open={!!skipTarget} onOpenChange={(v) => { if (!v) { setSkipTarget(null); setSkipReason(""); } }}>
        <DialogContent data-testid="skip-followup-dialog">
          <DialogHeader>
            <DialogTitle>Skip this follow-up?</DialogTitle>
          </DialogHeader>
          <DialogDescription className="text-sm text-slate-500 -mt-1">{skipTarget?.name ? <span className="font-medium text-slate-700">{skipTarget.name}</span> : "This project"} won't nag you again unless the client sends a new email. Add a reason so your team knows why.</DialogDescription>
          <Textarea autoFocus value={skipReason} onChange={(e) => setSkipReason(e.target.value)} rows={3}
            placeholder="e.g. Owner feedback is genuinely pending — they asked us to prioritize another project first." data-testid="skip-followup-reason" />
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setSkipTarget(null); setSkipReason(""); }} data-testid="skip-followup-cancel">Cancel</Button>
            <Button onClick={doSkip} data-testid="skip-followup-confirm" className="bg-rose-600 hover:bg-rose-700 text-white">Skip follow-up</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
